import { ReactComponent as InfoIcon } from "../../../../../icons/info-Simulator.svg";
import { useCallback, useEffect, useState } from "react";
import { getBessSurDefPeakHours } from "../../../../../Rest_api/RestApi";
import loader from "../../../../../components/recharts/loader.gif";
import PortfolioPiechart from "./PortfolioPiechart";

interface PeriodData {
    slot_range: string;
    avg_surplus: number;
    avg_deficit: number;
}

interface RatioData {
    surplus: number;
    deficit: number;
}

interface ApiResponse {
    session: string;
    session_id: number;
    scenario: number;
    start_date: string;
    end_date: string;
    period_data: {
        solar_hours: PeriodData;
        morning: PeriodData;
        evening: PeriodData;
    };
    surplus_deficit_ratio: RatioData;
}

export default function SurplusDeficitContributor({
    refreshKey,
    selectedBlock,
    startDate,
    endDate,
    scenarioId,
    setError,
}: {
    refreshKey: number;
    selectedBlock: any[];
    startDate: string;
    endDate?: string;
    scenarioId: number;
    setError?: any;
}) {
    const [data, setData] = useState<ApiResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const handleFetchData = useCallback(async () => {
        const effectiveEndDate = endDate || startDate;

        if (!startDate || scenarioId === undefined || scenarioId === null) return;

        try {
            setLoading(true);
            const res = await getBessSurDefPeakHours({
                start_date: startDate,
                end_date: effectiveEndDate,
                scenario_id: scenarioId.toString(),
            });

            if (res) {
                setData(res);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            if (setError) setError("An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    }, [scenarioId, startDate, endDate, setError, refreshKey]);

    useEffect(() => {
        handleFetchData();
    }, [handleFetchData]);

    // Transform data for Period Chart (Left) - using Manual List Style with Grouped Bars
    const periodItems = data
        ? [
            {
                name: "Solar Hours",
                surplus: data.period_data.solar_hours.avg_surplus,
                deficit: data.period_data.solar_hours.avg_deficit,
            },
            {
                name: "Morning Hours",
                surplus: data.period_data.morning.avg_surplus,
                deficit: data.period_data.morning.avg_deficit,
            },
            {
                name: "Evening Hours",
                surplus: data.period_data.evening.avg_surplus,
                deficit: data.period_data.evening.avg_deficit,
            },
        ]
        : [];

    const maxPeriodValue = Math.max(
        ...periodItems.map((d) => Math.max(d.surplus, d.deficit)),
        0
    ) || 1;

    return (
        <div className="col-span-1 md:col-span-12 flex flex-col md:flex-row gap-2 h-full w-full">
            {/* Left Card: Surplus Vs Deficit */}
            <div className="dash-card border border-[#DFEAF2] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] flex-1 h-full min-h-0 rounded-2xl p-3 overflow-hidden flex flex-col !min-h-[100px]">
                <div className="flex justify-start items-center gap-2 mb-3 border-b-2 border-[#DFEAF2] pb-1">
                    <h2 className="chartHeading !text-sm w-100 !text-[#34656D]">
                        Time-of-Day Surplus-Deficit Balance
                    </h2>
                    <span>
                        {/* <InfoIcon className="w-5 h-5 text-gray-500" /> */}
                    </span>
                </div>

                {loading ? (
                    <div className="flex-1 w-full bg-white flex items-center justify-center">
                        <img
                            src={loader}
                            width={200}
                            alt="loader"
                            className="block"
                        />
                    </div>
                ) : (
                    <div className="w-full h-full text-start overflow-hidden flex-1">
                        <div className="space-y-5 w-full h-full overflow-y-auto pr-2 custom-scrollbar">
                            {periodItems.map((item, index) => {
                                const surplusWidthPercent = (item.surplus / maxPeriodValue) * 100;
                                const deficitWidthPercent = (item.deficit / maxPeriodValue) * 100;

                                const isSurplusZero = item.surplus < 0.01;
                                const isDeficitZero = item.deficit < 0.01;

                                const effectiveSurplusWidth = isSurplusZero ? 0 : surplusWidthPercent;
                                const effectiveDeficitWidth = isDeficitZero ? 0 : deficitWidthPercent;

                                const isSurplusSmall = effectiveSurplusWidth < 30;
                                const isDeficitSmall = effectiveDeficitWidth < 30;

                                return (
                                    <div key={index} className="flex flex-col mb-1">
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-xs text-[#525062] font-medium w-40 truncate" title={item.name}>
                                                    {item.name}
                                                </span>
                                                <span className="text-[9px] text-[#8C8A96]">
                                                    {item.name === "Solar Hours" ? "(10:00 - 18:00)" :
                                                        item.name === "Morning Hours" ? "(06:00 - 10:00)" :
                                                            "(18:00 - 22:00)"}
                                                </span>
                                            </div>
                                            <div className="flex-1 ml-4">
                                                {/* Surplus Bar (Teal) */}
                                                <div className="relative rounded-lg h-6 overflow-hidden mb-1">
                                                    <div
                                                        className={`absolute top-0 left-0 h-6 bg-[#E3F1F3] flex items-center transition-all duration-500 rounded-r-lg ${isSurplusSmall ? "justify-start pl-4" : "justify-end pr-4"}`}
                                                        style={{ width: `${effectiveSurplusWidth}%` }}
                                                    >
                                                        {!isSurplusSmall && (
                                                            <span className="text-[#34656D] font-medium text-[10px]">
                                                                Surplus: {item.surplus.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isSurplusSmall && (
                                                        <span
                                                            className="absolute top-0 left-0 h-6 flex items-center text-[#34656D] font-medium text-[10px] ml-4"
                                                            style={{ left: `${effectiveSurplusWidth}%` }}
                                                        >
                                                            Surplus: {item.surplus.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Deficit Bar (Orange) */}
                                                <div className="relative rounded-lg h-6 overflow-hidden">
                                                    <div
                                                        className={`absolute top-0 left-0 h-6 bg-[#FCE9DE] flex items-center transition-all duration-500 rounded-r-lg ${isDeficitSmall ? "justify-start pl-4" : "justify-end pr-4"}`}
                                                        style={{ width: `${effectiveDeficitWidth}%` }}
                                                    >
                                                        {!isDeficitSmall && (
                                                            <span className="text-[#913A08] font-medium text-[10px]">
                                                                Deficit: {item.deficit.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isDeficitSmall && (
                                                        <span
                                                            className="absolute top-0 left-0 h-6 flex items-center text-[#913A08] font-medium text-[10px] ml-4"
                                                            style={{ left: `${effectiveDeficitWidth}%` }}
                                                        >
                                                            Deficit: {item.deficit.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Right Card: Surplus/Deficit Share */}
            <div className="dash-card border border-[#DFEAF2] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] flex-1 h-full min-h-0 rounded-2xl p-3 overflow-hidden flex flex-col !min-h-[100px]">
                <div className="flex justify-start items-center gap-2 mb-1 border-b-2 border-[#DFEAF2] pb-1">
                    <h2 className="chartHeading !text-sm w-100 !text-[#34656D]">
                        Surplus-Deficit Portfolio
                    </h2>
                    <span>
                        {/* <InfoIcon className="w-5 h-5 text-gray-500" /> */}
                    </span>
                </div>

                {loading ? (
                    <div className="flex-1 w-full bg-white flex items-center justify-center">
                        <img
                            src={loader}
                            width={200}
                            alt="loader"
                            className="block"
                        />
                    </div>
                ) : (
                    <div className="w-full h-full text-start overflow-hidden flex-1 flex items-center justify-center">
                        {data && <PortfolioPiechart data={[data.surplus_deficit_ratio]} chartHeight="100%" />}
                    </div>
                )}
            </div>
        </div>
    );
}
